import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, Modal, withStyles, RadioGroup, FormControlLabel, Radio } from '@material-ui/core';
import { viewModalStyles } from '../../styles';
import Moment from 'moment';
import { getDateNoTimezone } from '../../../../collums-components/helpers/timezone';
import CloseBtn from '../../../common/CloseBtn';
import {
    CustomAmountField,
    CustomInfoField,
    CustomTextField,
    CustomSelectField,
    CustomDatePickerField
} from '../../../common/FieldContainer/fields';
import Button from '@material-ui/core/Button';
import MembershipApi, { listMemberships } from '../../../../api/membershipApi';
import { toastr } from 'react-redux-toastr';
import { setListShouldUpdate } from '../../../../actions/invoiceActions';
import { useDispatch, useSelector } from 'react-redux';
import { listActiveClinics } from '../../../../collums-components/api/ClinicApi';
import { getCurrentClinicSelector } from '../../../../customSelectors/calendar';
import { links } from '../../../../collums-components/api/config';

const MembershipModal = ({ classes, showMembershipModal, setShowMembershipModal, customer }) => {
    const minDaysAhead = 0;
    const [quantity, setQuantity] = useState(12);
    const [paymentValue, setPaymentValue] = useState('');
    const [totalPaymentValue, setTotalPaymentValue] = useState(null);
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [membershipName, setMembershipName] = useState('');
    const [firstPaymentDate, setFirstPaymentDate] = useState(
        Moment()
            .add(minDaysAhead, 'days')
            .toDate()
    );
    const [membershipType, setMembershipType] = useState('defined');
    const dispatch = useDispatch();
    const currentClinic = useSelector(getCurrentClinicSelector);
    const [clinics, setClinics] = useState([]);
    const [selectedClinic, setSelectedClinic] = useState(null);
    const [memberships, setMemberships] = useState([]);
    const [selectedMembership, setSelectedMembership] = useState('');

    useEffect(() => {
        const fetchClinics = async () => {
            const clinicsList = await listActiveClinics();
            setClinics(clinicsList);
            if (currentClinic && clinicsList.length > 0) {
                const found = clinicsList.find(c => c.id === currentClinic);
                setSelectedClinic(found ? currentClinic : clinicsList[0]?.id);
            } else if (clinicsList.length > 0) {
                setSelectedClinic(clinicsList[0].id);
            }
        };
        fetchClinics();
    }, [currentClinic]);

    useEffect(() => {
        setSelectedMembership('');
        setPaymentValue('');
        if (selectedClinic) {
            listMemberships(selectedClinic)
                .then(setMemberships)
                .catch(() => setMemberships([]));
        } else {
            setMemberships([]);
        }
    }, [selectedClinic]);

    useEffect(() => {
        if (showMembershipModal) {
            setMembershipName('');
            setSelectedMembership('');
            setMembershipType('defined');
            setPaymentValue('');
            setQuantity(12);
            setTotalPaymentValue(null);
            setFirstPaymentDate(
                Moment()
                    .add(minDaysAhead, 'days')
                    .toDate()
            );
            if (currentClinic && clinics.length > 0) {
                const found = clinics.find(c => c.id === currentClinic);
                setSelectedClinic(found ? currentClinic : clinics[0]?.id);
            } else if (clinics.length > 0) {
                setSelectedClinic(clinics[0].id);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showMembershipModal]);
    let quantityOptions = Array.from(new Array(36), (_, i) => ({
        label: `${i + 1}`,
        value: `${i + 1}`
    }));
    quantityOptions.unshift({ label: 'Until client cancels', value: 0 });

    const updateTotalPayment = (paymentValue, quantity) => {
        setTotalPaymentValue((paymentValue * quantity).toFixed(2));
    };

    return (
        <Modal open={showMembershipModal} className={classes.modal}>
            <div className={classes.root} style={{ width: 700 }}>
                <div className={classes.closeBtn}>
                    <CloseBtn onClick={() => setShowMembershipModal(false)} />
                </div>
                <div className={classes.header}>
                    <p>Create New Membership Direct Debit</p>
                </div>

                <div style={{ margin: '0 50px 20px 50px', fontSize: 14, lineHeight: 1.6 }}>
                    <p>Create a Direct Debit to pay a monthly amount into the client&apos;s account.</p>
                    <p>
                        This can be to pay towards a Membership that you have created in{' '}
                        <a href={`${links.adminUrl}/membership`} target="_blank" rel="noopener noreferrer">
                            Admin
                        </a>{' '}
                        with specific services to be redeemed.
                    </p>
                    <p>
                        Or it can be a simple monthly payment into their account, unrelated to any pre-defined services.
                    </p>
                </div>

                <div className={classes.content} style={{ fontSize: 18 }}>
                    <form>
                        <Grid container style={{ margin: '0 50px 20px 50px' }}>
                            <Grid item xs={12} style={{ marginBottom: 16 }}>
                                <RadioGroup value={membershipType} onChange={e => setMembershipType(e.target.value)}>
                                    <FormControlLabel
                                        value="defined"
                                        control={<Radio color="primary" />}
                                        label={
                                            <span style={{ fontSize: 18 }}>
                                                Membership created with defined services
                                            </span>
                                        }
                                    />
                                    <FormControlLabel
                                        value="regular"
                                        control={<Radio color="primary" />}
                                        label={
                                            <span style={{ fontSize: 18 }}>
                                                Regular payment into account only - No specified services
                                            </span>
                                        }
                                    />
                                </RadioGroup>
                            </Grid>
                            <CustomInfoField
                                label="Name"
                                value={customer ? `${customer.firstName} ${customer.surname}` : ''}
                            />
                            <CustomSelectField
                                label="Clinic"
                                value={selectedClinic || ''}
                                onChange={event => setSelectedClinic(event.target.value)}
                                options={clinics.map(c => ({ value: c.id, label: c.accountName }))}
                            />
                            {membershipType === 'regular' ? (
                                <CustomTextField
                                    label="Membership name"
                                    value={membershipName}
                                    onChange={event => {
                                        setMembershipName(event.target.value);
                                    }}
                                />
                            ) : (
                                <CustomSelectField
                                    label="Membership"
                                    value={selectedMembership}
                                    onChange={event => {
                                        const membershipId = event.target.value;
                                        setSelectedMembership(membershipId);
                                        const selected = memberships.find(m => (m.id || m._id) === membershipId);
                                        if (selected) {
                                            const location = (selected.locations || []).find(
                                                l => (l.clinic || '').toString() === (selectedClinic || '').toString()
                                            );
                                            const price = location ? location.salePrice : selected.salePrice || 0;
                                            setPaymentValue(price ? price.toFixed(2) : '');
                                            setQuantity(0);
                                            setTotalPaymentValue(null);
                                        }
                                    }}
                                    options={memberships.map(m => ({ value: m.id || m._id, label: m.name }))}
                                />
                            )}
                            {membershipType === 'defined' ? (
                                <CustomInfoField
                                    label="Monthly amount"
                                    value={paymentValue ? `£${paymentValue}` : ''}
                                />
                            ) : (
                                <CustomAmountField
                                    label="Monthly amount"
                                    value={paymentValue}
                                    onChange={event => {
                                        const value = event.target.value;
                                        if (/^\d*(\.\d{0,2})?$/.test(value)) {
                                            setPaymentValue(value);
                                            updateTotalPayment(value, quantity);
                                        }
                                    }}
                                />
                            )}
                            {membershipType === 'regular' && (
                                <CustomSelectField
                                    label="No of monthly payments"
                                    value={quantity}
                                    onChange={event => {
                                        setQuantity(event.target.value);
                                        updateTotalPayment(paymentValue, event.target.value);
                                    }}
                                    options={quantityOptions}
                                />
                            )}

                            <CustomDatePickerField
                                label="Date of first payment"
                                value={firstPaymentDate}
                                onChange={value => {
                                    if (!value) return;
                                    const date = Moment.utc(getDateNoTimezone(value));
                                    setFirstPaymentDate(date);
                                }}
                                minDate={
                                    process.env.NODE_ENV === 'development'
                                        ? undefined
                                        : Moment()
                                              .add(minDaysAhead, 'days')
                                              .toDate()
                                }
                            />

                            {membershipType === 'regular' && quantity > 0 && paymentValue > 0 && (
                                <CustomInfoField
                                    label="Total payment amount"
                                    value={totalPaymentValue === null ? '' : '£' + totalPaymentValue}
                                />
                            )}
                        </Grid>
                        <div style={{ fontSize: 14, margin: 10 }}>
                            Clicking &quot;Create Direct Debit&quot; will send an email to your client with a link to
                            set up the Direct Debit. Once submitted, £1.00 will be taken from their account confirming
                            the DD. The first payment will then be taken less the £1.00.
                        </div>
                        <div className={classes.actions} style={{ width: '100%', margin: '20px auto' }}>
                            <Button
                                className={classes.cancelButton}
                                key="cancel"
                                variant="outlined"
                                onClick={() => setShowMembershipModal(false)}
                            >
                                Back
                            </Button>

                            <Button
                                className={classes.confirmButton}
                                disabled={
                                    buttonDisabled ||
                                    !paymentValue ||
                                    (membershipType === 'defined' && !selectedMembership) ||
                                    (membershipType === 'regular' && !membershipName.trim())
                                }
                                onClick={async () => {
                                    setButtonDisabled(true);
                                    const data = {
                                        firstPayment: firstPaymentDate,
                                        amount: paymentValue,
                                        customer: customer.id,
                                        numberOfPayments: quantity,
                                        clinic: selectedClinic
                                    };
                                    if (membershipType === 'defined') {
                                        data.membership = selectedMembership;
                                        const selected = memberships.find(m => (m.id || m._id) === selectedMembership);
                                        data.name = selected ? selected.name : '';
                                    } else {
                                        data.name = membershipName;
                                    }
                                    const response = await MembershipApi.createCustomerMembership(data);
                                    if (!response.success) {
                                        toastr.error(response.message);
                                        setButtonDisabled(false);
                                    } else {
                                        if (membershipType === 'defined') {
                                            const selected = memberships.find(
                                                m => (m.id || m._id) === selectedMembership
                                            );
                                            const hasImmediateService = selected?.services?.some(
                                                svc => (svc.availableFrom || 'immediately') === 'immediately'
                                            );
                                            if (hasImmediateService) {
                                                toastr.success('Membership added to client card');
                                            }
                                        }
                                        toastr.success('Direct Debit request has been sent');
                                        setShowMembershipModal(false);
                                        setButtonDisabled(false);
                                        dispatch(setListShouldUpdate(true));
                                    }
                                }}
                            >
                                Create Direct Debit
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </Modal>
    );
};

MembershipModal.propTypes = {
    classes: PropTypes.object.isRequired,
    showMembershipModal: PropTypes.bool.isRequired,
    setShowMembershipModal: PropTypes.func.isRequired,
    customer: PropTypes.object.isRequired
};

export default withStyles(viewModalStyles)(MembershipModal);
